'use client';

import { useEffect, useRef, type ReactNode } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

type Props = {
    title: ReactNode;
    description: ReactNode;
    /** Page content, rendered on the white card that slides up over the hero */
    children: ReactNode;
};

// Dark hero card that lags behind the scroll and shrinks while the white
// content card slides up over it (stacked-card scroll).
export default function StackedHero({ title, description, children }: Props) {
    const heroRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const hero = heroRef.current;
        if (!hero || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        gsap.registerPlugin(ScrollTrigger);
        const ctx = gsap.context(() => {
            gsap.fromTo(
                hero,
                { y: 0, scale: 1, transformPerspective: 1200 },
                {
                    y: () => window.innerHeight * 0.64,
                    scale: 0.71,
                    ease: 'none',
                    scrollTrigger: {
                        trigger: hero,
                        start: 'top top',
                        end: () => `+=${window.innerHeight}`,
                        scrub: true,
                        invalidateOnRefresh: true,
                    },
                },
            );
        });
        return () => ctx.revert();
    }, []);

    return (
        <>
            {/* ── Hero ── */}
            <section style={{ padding: '12px 12px 0' }}>
                <div
                    ref={heroRef}
                    style={{
                        position: 'relative',
                        background: '#171717',
                        borderRadius: '32px 32px 0 0',
                        padding: 'clamp(170px, 18vw, 230px) 24px clamp(140px, 14vw, 200px)',
                        textAlign: 'center',
                        overflow: 'hidden',
                        transformOrigin: '50% 50%',
                        willChange: 'transform',
                    }}
                >
                    {/* Faint grid, fading out towards the edges */}
                    <div
                        aria-hidden
                        style={{
                            position: 'absolute', inset: 0,
                            backgroundImage: 'linear-gradient(rgba(255,255,255,0.045) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.045) 1px, transparent 1px)',
                            backgroundSize: '96px 96px',
                            backgroundPosition: 'center center',
                            maskImage: 'radial-gradient(ellipse 70% 65% at 50% 50%, #000 30%, transparent 100%)',
                            WebkitMaskImage: 'radial-gradient(ellipse 70% 65% at 50% 50%, #000 30%, transparent 100%)',
                        }}
                    />
                    <div style={{ position: 'relative', maxWidth: 1100, margin: '0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'clamp(20px, 2.4vw, 32px)' }}>
                        <h1 style={{
                            fontFamily: 'var(--font-clash), "Clash Display", sans-serif', fontWeight: 600,
                            fontSize: 'clamp(40px, 6.2vw, 96px)', letterSpacing: '-0.01em',
                            color: '#ffffff', lineHeight: 1.05, margin: 0,
                        }}>
                            {title}
                        </h1>
                        <p style={{
                            fontFamily: 'var(--font-manrope), Manrope, sans-serif', fontWeight: 400,
                            fontSize: 'clamp(16px, 1.55vw, 24px)', lineHeight: 1.7, letterSpacing: '0.01em',
                            color: 'rgba(255,255,255,0.55)', maxWidth: 880, margin: 0,
                        }}>
                            {description}
                        </p>
                    </div>
                </div>
            </section>

            {/* ── Content card ── */}
            <section style={{
                position: 'relative',
                zIndex: 1,
                margin: '-64px 12px 0',
                background: '#ffffff',
                borderRadius: 32,
            }}>
                {children}
            </section>
        </>
    );
}
